import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Plus, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCustomers, Customer } from '@/hooks/useCustomers';
import { CreateCustomerDialog } from '@/components/CreateCustomerDialog';
import SimpleCustomerDropdown from '@/components/SimpleCustomerDropdown';
import { safeArray, safeFind } from '@/utils/safeArray';

interface CompactCustomerSelectionProps {
  selectedCustomerId?: string;
  onCustomerSelect: (customer: Customer | null) => void;
  disabled?: boolean;
}

export function CompactCustomerSelection({
  selectedCustomerId,
  onCustomerSelect,
  disabled = false
}: CompactCustomerSelectionProps) {
  const { customers, isLoading, createCustomer } = useCustomers();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Debug logging with proper useEffect to prevent re-render loops
  useEffect(() => {
    console.log('CompactCustomerSelection: customers data:', customers, 'isLoading:', isLoading);
  }, [customers, isLoading]);

  // Use safe array utilities to prevent iteration errors - memoized to prevent re-renders
  const safeCustomers = useMemo(() => safeArray(customers), [customers]);
  const selectedCustomer = useMemo(() => 
    safeFind(customers, c => c?.id === selectedCustomerId), 
    [customers, selectedCustomerId]
  );

  // Filter customers based on search term (minimum 3 characters)
  const filteredCustomers = searchTerm.length >= 3 
    ? safeCustomers.filter(customer => {
        if (!customer || typeof customer.name !== 'string') {
          console.warn('CompactCustomerSelection: Invalid customer object:', customer);
          return false;
        }
        return customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : safeCustomers;

  const handleCustomerSelect = (customer: Customer) => {
    onCustomerSelect(customer);
    setOpen(false);
    setSearchTerm('');
  };

  const handleCreateCustomer = (customerData: any) => {
    createCustomer(customerData);
    setShowCreateDialog(false);
  };

  const handleClearSelection = () => {
    onCustomerSelect(null);
  };

  return (
    <div className="space-y-3">
      {/* Selected Customer Display */}
      {selectedCustomer ? (
        <Card className="p-3 bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">{selectedCustomer.name}</h4>
                {selectedCustomer.email && (
                  <p className="text-xs text-muted-foreground truncate">{selectedCustomer.email}</p>
                )}
              </div>
              <Badge variant={selectedCustomer.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                {selectedCustomer.status}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="h-8 text-xs"
              disabled={disabled}
            >
              Change
            </Button>
          </div>
        </Card>
      ) : (
        /* Simple Customer Dropdown */
        <SimpleCustomerDropdown
          value={selectedCustomerId}
          onChange={(customerId) => {
            const customer = safeFind(customers, c => c?.id === customerId);
            onCustomerSelect(customer || null);
          }}
          onCreateNew={() => setShowCreateDialog(true)}
          placeholder="Select customer..."
          className="w-full"
        />
      )}

      {/* Create Customer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <CreateCustomerDialog
            onCustomerCreated={(customer) => {
              onCustomerSelect(customer);
              setShowCreateDialog(false);
            }}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}